import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { Truck, Crosshair, FoldHorizontal, Cylinder, Construction, SprayCan, Shield, FileText, ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export default function Capabilities() {
  const { t } = useLanguage();

  const capabilities: {
    icon: LucideIcon;
    overline: string;
    titleKey: string;
    descKey: string;
    items: string[];
    image: string;
    link: string;
    buyerValue?: string;
  }[] = [
    {
      icon: Truck, overline: "SUPPLY", titleKey: "industrialSupplyCap", descKey: "industrialSupplyCapDesc",
      items: ["Project materials", "BOQ-based sourcing", "Urgent requirements", "Replacement items"],
      image: "/images/electrical-supply.jpg", link: "/products",
    },
    {
      icon: Crosshair, overline: "CNC CUTTING", titleKey: "cncLaserCutting", descKey: "cncLaserCuttingDesc",
      items: ["Mounting plates", "Base & anchor plates", "Gussets & stiffeners", "Rails & covers"],
      image: "/images/cnc-laser-cutting.jpg", link: "/cnc",
      buyerValue: "Reduces rework during fabrication and installation.",
    },
    {
      icon: FoldHorizontal, overline: "BENDING", titleKey: "pressBrakeBending", descKey: "pressBrakeBendingDesc",
      items: ["L, U, Z brackets", "Cantilever brackets", "U & C channels", "Hoods & enclosures"],
      image: "/images/press-brake-bending.jpg", link: "/cnc",
      buyerValue: "Adds strength and shape to flat steel parts.",
    },
    {
      icon: Cylinder, overline: "TUBE FAB", titleKey: "tubePipeFab", descKey: "tubePipeFabDesc",
      items: ["Instrument stands", "Lighting & CCTV poles", "Wall arms", "Handrails & barriers"],
      image: "/images/tube-fabrication.jpg", link: "/cnc",
      buyerValue: "Creates strong support items for infrastructure projects.",
    },
    {
      icon: Construction, overline: "HEAVY FAB", titleKey: "weldingHeavyFab", descKey: "weldingHeavyFabDesc",
      items: ["Equipment skids", "Structural frames", "Platforms & walkways", "Stairs & ladders"],
      image: "/images/steel-fabrication.jpg", link: "/steel",
      buyerValue: "Combines components into complete project-ready assemblies.",
    },
    {
      icon: SprayCan, overline: "POWDER", titleKey: "powderCoatingCap", descKey: "powderCoatingCapDesc",
      items: ["Fabricated steel", "Brackets & frames", "Panels & covers", "Custom items"],
      image: "/images/powder-coating.jpg", link: "/coating",
    },
    {
      icon: Shield, overline: "EPOXY", titleKey: "epoxyCoatingCap", descKey: "epoxyCoatingCapDesc",
      items: ["Industrial components", "Steel protection", "Project materials", "Fabricated parts"],
      image: "/images/epoxy-coating.jpg", link: "/coating",
    },
    {
      icon: FileText, overline: "PROJECT", titleKey: "projectSupportCap", descKey: "projectSupportCapDesc",
      items: ["Quotation requests", "Drawing review", "BOQ supply support", "Delivery coordination"],
      image: "/images/project-support.jpg", link: "/contact",
    },
  ];

  return (
    <>
      <PageHeader titleKey="capabilitiesTitle" subtitleKey="capabilitiesSubtitle" />

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div
                  key={cap.titleKey}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.07, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Link to={cap.link} className="group block h-full">
                    <div className="h-full bg-white rounded-xl overflow-hidden border border-muted-border/50 transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
                      <div className="relative h-44 overflow-hidden">
                        <img src={cap.image} alt={t(cap.titleKey)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                        <div className="absolute inset-0 bg-primary-deep/40 group-hover:bg-primary-deep/20 transition-colors duration-300" />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-xs font-semibold text-primary-deep uppercase tracking-wider">{cap.overline}</span>
                        </div>
                        <div className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <ArrowRight className="w-5 h-5 text-primary-deep" />
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-4 h-4 text-gold" />
                          <h3 className="font-display font-bold text-base text-graphite group-hover:text-secondary-blue transition-colors">{t(cap.titleKey)}</h3>
                        </div>
                        <p className="text-sm text-steel mb-3 leading-relaxed">{t(cap.descKey)}</p>
                        <ul className="space-y-1">
                          {cap.items.map((item) => (
                            <li key={item} className="flex items-center gap-1.5 text-xs text-steel">
                              <span className="w-1 h-1 rounded-full bg-secondary-blue shrink-0" />{item}
                            </li>
                          ))}
                        </ul>
                        {cap.buyerValue && (
                          <p className="mt-3 text-xs text-steel bg-steel-light rounded-lg p-2.5 border-l-2 border-gold">
                            <span className="font-semibold text-primary-deep">Value: </span>{cap.buyerValue}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection titleKey="needCustom" descKey="needCustomDesc" bgColor="dark" />
    </>
  );
}
